import { useEffect, useState } from 'react';
import usePrevious from 'react-use/esm/usePrevious';
import {
  createPixelDataSharedWorker,
  providerMap,
} from 'modules/viewer/workers/PixelDataSharedWorker';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import { AppRoutePath } from 'config/constants';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import { atom, useRecoilState, useRecoilValue } from 'recoil';
import { generationStateAtom } from './EscapeHatch/state';
import { useEscapeHatchCoordination } from './EscapeHatch/coordination';
import { selectGeneration } from './EscapeHatch/policy';
import { awaitPriorGenerationDrained } from './EscapeHatch/generationDrain';
import {
  findStalledLoadingStack,
  updateFirstSeenLoadingAt,
  type LoadingProviderSnapshot,
} from './EscapeHatch/idleDeliveryWatchdog';
import type { RecoilState } from 'recoil';
import { openWindowEvent, dispatchExtensionEvent } from 'domains/extension/extensionEventCreators';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { env } from 'config/env';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags/suspense';
import { logger } from 'modules/logger';
const terminateSharedWorkerConnection = (worker: PixelDataSharedWorker | null) => {
  if (worker == null) {
    return;
  }

  // postMessage as well as close can throw on an already-closed MessagePort, so guard both.
  try {
    worker.port.postMessage({ type: 'terminate-connection' });
    worker.port.close();
  } catch (error) {
    logger.info(
      `failed to terminate pixel data shared worker port: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};

export const pixelWorkerOpenedState: RecoilState<boolean> = atom({
  key: 'viewer.dre.pixelWorkerOpenState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const useOpenPixelDataSharedWorker = (workerKey: string): PixelDataSharedWorker | null => {
  const [hasWorkerOpened, setWorkerOpened] = useRecoilState(pixelWorkerOpenedState);
  const useDedupedPixelWorker = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  // VX-4156 kill-switch: when off, the client does not post `enable-pruning` (so the SharedWorker's
  // prune() never marks this port eligible to reclaim) and the watchdog below is disabled.
  // Worker-side prune()/reconcile is unchanged — it cannot read flags — but with no port opted in it
  // has nothing to act on for this connection.
  const enablePortPruning = useFeatureFlagEnabled(FF.VIEWER_SW_PORT_PRUNING);
  // VX-4302: connect at the current generation when the escape hatch is enabled, so a committed bump
  // (a brand-new worker name) is picked up. When off, the generation stays 0 (the bare name) and
  // behaviour is identical to today. selectGeneration is the shared flag-gate the host window uses
  // too, so the two windows never disagree on which worker to connect to.
  const generationBumpEnabled = useFeatureFlagEnabled(FF.VIEWER_SW_GENERATION_BUMP);
  const generationState = useRecoilValue(generationStateAtom);
  const activeGeneration = selectGeneration(generationBumpEnabled, generationState);

  useEffect(() => {
    if (!hasWorkerOpened) {
      const isFocused = document.hasFocus();
      if (isWorkspaceManagerAvailable()) {
        const { protocol, host } = window.location;

        const url = `${protocol}//${host}${AppRoutePath.PIXEL_WORKER}`;
        dispatchExtensionEvent(
          openWindowEvent({
            url,
            windowConfig: {
              // `focused` disrupts functionality of `minimized` state
              // see https://issues.chromium.org/issues/40204304
              focused: null,
              top: null,
              left: null,
              width: null,
              height: null,
              state: 'minimized',
            },
          })
        );
        // generally not needed, but a fallback in case the new window somehow steals focus
        if (isFocused) {
          window.focus();
        }
      } else {
        if (env.STORYBOOK === 'true') {
          return;
        }

        window.open(
          AppRoutePath.PIXEL_WORKER,
          AppRoutePath.PIXEL_WORKER,
          'noopener=yes, width=300, height=150, left=0, top=0, toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no'
        );
        if (isFocused) {
          window.focus();
        }
      }

      // Viewer functionality is absolutely dependent on the Shared Worker opening.
      // In the event the dedicated window cannot open, update the state so that
      // the Viewer windows will start talking to the Shared Worker, which will
      // start a new Shared Worker if not already present.
      setTimeout(() => {
        setWorkerOpened(true);
      }, 1500);
    }
  }, [hasWorkerOpened, setWorkerOpened]);

  const [pixelDataSharedWorkerMap, setPixelDataSharedWorkerMap] = useState<{
    [caseSmid: string]: PixelDataSharedWorker | null;
  }>({ '': null });

  const matchingWorker = pixelDataSharedWorkerMap[workerKey] ?? null;

  // VX-4385: while a generation bump is in flight, the prior generation we are waiting to confirm
  // drained before we connect at the new generation; null when no bump-driven gate is pending. This
  // gates ONLY the bump-driven recreation — the watchdog (same-generation) reconnect path never sets
  // it, so a severed-port recovery still reconnects immediately.
  const [drainGatePriorGeneration, setDrainGatePriorGeneration] = useState<number | null>(null);

  // VX-4302 #5b: the automatic ladder trigger. Counts watchdog recreates that fail to restore
  // delivery (the watchdog now fires on VX-4284's delivery-stall pong-withhold) and, when quorum of
  // eligible observers agree, commits a generation bump. No-op while the flag is off. The callback is
  // stable (a useRecoilCallback) except when `enabled` toggles, so the watchdog effect can depend on
  // it directly.
  const { reportFailedRecreate } = useEscapeHatchCoordination({
    matchingWorker,
    enabled: generationBumpEnabled,
  });

  useEffect(() => {
    // VX-4385: hold off creating the new-generation worker (which would immediately start decoding a
    // fresh OpenJPH WASM heap) until the prior generation has drained. The drain-await effect below
    // clears the gate on the retirement ack or a bounded deadline, after which this effect re-runs and
    // connects. The initial connection and the watchdog reconnect leave the gate null, so neither is
    // delayed.
    if (drainGatePriorGeneration != null) return;
    if (hasWorkerOpened && matchingWorker == null) {
      setPixelDataSharedWorkerMap({
        [workerKey]: createPixelDataSharedWorker(
          useDedupedPixelWorker,
          undefined,
          enablePortPruning,
          activeGeneration
        ),
      });
      logger.info(`new shared worker connection for ${workerKey}`);
    }
  }, [
    hasWorkerOpened,
    useDedupedPixelWorker,
    enablePortPruning,
    matchingWorker,
    workerKey,
    activeGeneration,
    drainGatePriorGeneration,
  ]);

  // VX-4302: a committed generation bump — from this tab, or message-driven via the
  // BroadcastChannel-synced generation atom from another tab — drops the old-generation port so the
  // create effect above reconnects at the new generation. Reuses the load-bearing `worker -> null ->
  // worker` transition that useReinitializeProvidersOnWorkerChange keys on (VX-4223). There is no
  // timer in this path, so a backgrounded tab reconnects as soon as it processes the message.
  const previousGeneration = usePrevious(activeGeneration);
  useEffect(() => {
    // usePrevious is undefined on the first render and auto-advances every render, so a generation
    // change observed while the worker is null is naturally "consumed" without a spurious reconnect.
    if (previousGeneration === undefined || previousGeneration === activeGeneration) return;
    if (matchingWorker == null) return;
    logger.info(`SW generation -> ${activeGeneration} for workerKey=${workerKey}; reconnecting`);
    // VX-4385: gate the reconnect on the prior generation draining (set the gate to the generation we
    // are leaving). The create effect skips while this is set; the drain-await effect clears it once
    // the prior generation acks retirement or the deadline elapses, so the new generation never starts
    // decoding while the old generation's WASM heap is still resident.
    setDrainGatePriorGeneration(previousGeneration);
    // Null the worker; the [matchingWorker] cleanup below performs the single terminate/close so the
    // port is not closed twice (a second postMessage on a closed port can throw).
    setPixelDataSharedWorkerMap({ [workerKey]: null });
  }, [activeGeneration, previousGeneration, matchingWorker, workerKey]);

  // VX-4385: resolve the bump-driven drain gate. Waits for the prior generation to confirm it has
  // released its memory (the retirement ack) or for a bounded deadline, then clears the gate so the
  // create effect connects at the new generation. Bounded so recovery is never wedged if no prior
  // worker is listening (e.g. it was already gone, or BroadcastChannel is unavailable).
  useEffect(() => {
    if (drainGatePriorGeneration == null) return;
    let cancelled = false;
    void awaitPriorGenerationDrained(drainGatePriorGeneration).then(() => {
      if (!cancelled) setDrainGatePriorGeneration(null);
    });
    return () => {
      cancelled = true;
    };
  }, [drainGatePriorGeneration]);

  // cancel the current connection if it is replaced
  useEffect(() => {
    return () => {
      terminateSharedWorkerConnection(matchingWorker);
    };
  }, [matchingWorker]);

  // VX-4156: ping/pong watchdog. If two consecutive pings receive no pong, null out the matching
  // worker so the existing effect recreates it. Gated by the VIEWER_SW_PORT_PRUNING kill-switch:
  // when off, the watchdog never arms and the worker is never recreated on a missed pong.
  useEffect(() => {
    if (!enablePortPruning || matchingWorker == null) return;

    let missedPongCount = 0;
    let pendingPongTimeoutId: ReturnType<typeof setTimeout> | null = null;
    // Declared before recreateWorker so the teardown can stop the ping loop immediately; assigned
    // when the interval is armed below.
    let pingIntervalId: ReturnType<typeof setInterval> | null = null;
    // VX-4395: first-observed-loading timestamps for the idle-delivery check below, scoped to this
    // connection's lifetime (a fresh effect run gets a fresh map, same as missedPongCount).
    const firstSeenLoadingAt = new Map<string, number>();

    // Null out the matching worker so the create effect rebuilds it. This null transition is
    // load-bearing: useReinitializeProvidersOnWorkerChange treats oldWorker -> null -> newWorker as
    // the recovery signal that rebinds existing providers to the new port. A rebuild that skips null
    // would look like referential churn and the page would silently stop self-healing (VX-4223). The
    // [matchingWorker] cleanup below performs the single terminate/close so the port is not closed
    // twice (a second postMessage on a closed port can throw). Shared by the missed-pong path and the
    // worker-initiated pong-rejected path below.
    const recreateWorker = () => {
      // Stop the ping loop before closing the port so the next 30s tick can't postMessage on the
      // just-terminated port (the effect cleanup also clears it, but only after the next render).
      if (pingIntervalId != null) {
        clearInterval(pingIntervalId);
        pingIntervalId = null;
      }
      if (pendingPongTimeoutId != null) {
        clearTimeout(pendingPongTimeoutId);
        pendingPongTimeoutId = null;
      }
      missedPongCount = 0;
      setPixelDataSharedWorkerMap({ [workerKey]: null });
    };

    const onMessage = (e: MessageEvent) => {
      const data = (e as MessageEvent<{ type?: string; reason?: string }>).data;
      const type = data?.type;
      if (type === 'pong') {
        if (pendingPongTimeoutId != null) {
          clearTimeout(pendingPongTimeoutId);
          pendingPongTimeoutId = null;
        }
        missedPongCount = 0;
        return;
      }
      // VX-4284: the worker has detected its own delivery wedge (orphaned port, or frame delivery
      // stalled) and is asking to be recreated now, rather than making us wait out two missed pongs
      // (~80s). This is an explicit worker decision, not a possibly-throttled missed timer, so we
      // recover immediately regardless of tab visibility.
      if (type === 'pong-rejected') {
        const reason = data?.reason ?? 'unknown';
        logger.warn(`SW pong rejected (${reason}) for workerKey=${workerKey}; recreating worker`);
        // VX-4388: a pong-rejected means the worker itself declares this connection wedged — the same
        // "recreate didn't restore delivery" signal the missed-pong path feeds to the ladder, only sooner
        // and more explicit. Count it too (before recreating) so a wedge that a same-generation recreate can't fix
        // escalates to a generation bump instead of livelocking on the same gen, spawning duplicate
        // same-generation workers (allied-beige-superior). observeFrame resets the counter on recovery, so
        // a healthy self-heal (a re-registered orphaned port) never accumulates. No-op while the flag is off.
        reportFailedRecreate();
        recreateWorker();
      }
    };
    matchingWorker.port.addEventListener('message', onMessage);

    // VX-4223: a pong timeout armed while hidden can fire just after the tab becomes visible —
    // its lateness is background timer throttling, not a severed port. On the hidden->visible
    // transition, discard that stale timeout and reset the miss count so recovery requires fresh
    // consecutive misses observed entirely while visible (the in-callback guard below covers
    // timeouts that fire while still hidden).
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendingPongTimeoutId != null) {
        clearTimeout(pendingPongTimeoutId);
        pendingPongTimeoutId = null;
      }
      missedPongCount = 0;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    pingIntervalId = setInterval(() => {
      try {
        matchingWorker.port.postMessage({ type: 'ping' });
      } catch {
        // port may be closed mid-cycle — treat as a missed pong
      }
      pendingPongTimeoutId = setTimeout(() => {
        // VX-4223: a hidden tab has throttled/frozen timers, so a late pong is expected and
        // not a sign of a severed port. Don't count a miss while hidden, and clear any miss
        // accrued before hiding so a stale pre-hide miss can't combine with a post-unhide one
        // to recreate a still-healthy connection. Require fresh consecutive misses once visible.
        if (document.visibilityState === 'hidden') {
          missedPongCount = 0;
          return;
        }
        missedPongCount += 1;
        if (missedPongCount >= 2) {
          logger.warn(`SW port stale for workerKey=${workerKey}; recreating worker`);
          // VX-4302 #5b: this recreate did not restore delivery — count it toward the ladder. At
          // quorum the coordination commits a generation bump. No-op while the flag is off.
          reportFailedRecreate();
          recreateWorker();
        }
      }, 10_000);

      // VX-4395: independent of ping/pong health — a worker can pong healthy (activeRequests===0
      // on its side) while a stack this page is still waiting on has silently stopped receiving
      // frames (the "idle-but-healthy" wedge; VX-4284's worker-side gate cannot see it because
      // nothing is outstanding from the worker's point of view). Checked on the same 30s cadence,
      // from the page's own delivery clock, so a worker that has gone quiet cannot self-report
      // healthy.
      const now = Date.now();
      const loadingStackSmids = new Set<string>();
      const loadingProviders: LoadingProviderSnapshot[] = [];
      providerMap.forEach((provider) => {
        if (!provider.isLoading()) return;
        const stackSmid = provider.stack.smid;
        loadingStackSmids.add(stackSmid);
        loadingProviders.push({
          stackSmid,
          msSinceLastFrameDelivered: provider.msSinceLastFrameDelivered(),
        });
      });
      updateFirstSeenLoadingAt(loadingStackSmids, firstSeenLoadingAt, now);
      const stalledStackSmid = findStalledLoadingStack(loadingProviders, firstSeenLoadingAt, now);
      if (stalledStackSmid != null) {
        logger.warn(
          `SW idle-but-healthy delivery stall on stack ${stalledStackSmid} for workerKey=${workerKey}; recreating worker`
        );
        // Same ladder-feeding signal as the missed-pong and pong-rejected paths: this recreate did
        // not (yet) prove delivery restored, so count it toward the escalation quorum.
        reportFailedRecreate();
        recreateWorker();
      }
    }, 30_000);

    return () => {
      if (pingIntervalId != null) clearInterval(pingIntervalId);
      if (pendingPongTimeoutId != null) clearTimeout(pendingPongTimeoutId);
      matchingWorker.port.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enablePortPruning, matchingWorker, workerKey, reportFailedRecreate]);

  // cancel the page's current connection as the page closes
  useEffect(() => {
    function terminateConnection() {
      terminateSharedWorkerConnection(matchingWorker);
    }

    window.addEventListener('beforeunload', terminateConnection);
    return () => window.removeEventListener('beforeunload', terminateConnection);
  }, [matchingWorker]);

  return matchingWorker;
};
